import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Home as IconHome } from 'react-feather'
import { Link, useHistory } from 'react-router-dom'
import _ from 'lodash'
import * as yup from 'yup'
import styled from 'styled-components'
import { shade } from 'polished'
import useAxios from 'axios-hooks'
import { toast } from 'react-toastify'
import { Formik, Form, useFormikContext, Field as FormikField } from 'formik'
import { CardTitle, Input, Row, Button, CustomInput } from 'reactstrap'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'

import { Col, LoadableCard, Show } from '~/components/commons'
import Breadcrumb from '~/components/Breadcrumb'
import { TableContainer, validations } from './commons'
import { Field } from '../Posts/commons'
import UploadPhoto from '~/components/ecto/UploadPhoto'

import {
  defaultUserPermissions,
  defaultUserPermissionIcons,
} from '~/constants/users'
import { Alert } from '~/components/Alert'
import { ADMIN, CLIENT_ADMIN, SUPER_ADMIN } from '~/constants/roles'

const initialValues = {
  user: {
    name: '',
    email: '',
    password: '',
    userPermission: {},
    permissions: {},
    userNotification: {},
  },
  passwordConfirmation: '',
  avatarImage: '',
}

const validationSchema = () =>
  yup.lazy(({ user }) =>
    yup.object().shape({
      user: !user.password ? validations.userWithoutPass : validations.user,
      passwordConfirmation: user.password
        ? validations.passwordConfirmation
        : null,
    })
  )

const FormContext = React.createContext()

function PermissionsTable() {
  const { setFieldValue, values } = useFormikContext()
  const { userPermission, userIsAppAdmin, userIsAdmin } = useContext(
    FormContext
  )
  const formPermissions = values?.user?.userPermission
  const permissions = [...defaultUserPermissions].filter(permissionKey => {
    return userIsAppAdmin || userIsAdmin || userPermission[permissionKey]
  })

  return (
    <div style={{ flexGrow: '1' }}>
      <h3>
        <FormattedMessage id="Permissions" />
      </h3>
      <TableContainer style={{ marginTop: '10px' }}>
        <div className="table-responsive">
          <table className="table table-borderless table-striped mb-0">
            <tbody>
              {permissions?.map((permissionKey, index) => {
                const Icon = defaultUserPermissionIcons[permissionKey]
                return (
                  <tr key={index}>
                    <td
                      className="pl-2"
                      style={{
                        textTransform: 'capitalize',
                        fontSize: '16px',
                        fontWeight: 500,
                        fontFamily: 'Montserrat',
                      }}
                    >
                      <div
                        className="d-flex w-100 h-100 align-items-center"
                        style={{ gap: '1em' }}
                      >
                        <Icon size="20" />
                        <FormattedMessage id={_.upperFirst(permissionKey)} />
                      </div>
                    </td>

                    {(userIsAppAdmin || userIsAdmin) && (
                      <td key={index}>
                        <div className="custom-checkbox custom-control d-flex justify-content-end">
                          <CustomInput
                            type="switch"
                            id={`user-userPermission-${permissionKey}`}
                            name={`user.userPermission.${permissionKey}`}
                            checked={formPermissions[permissionKey]}
                            onChange={() =>
                              setFieldValue(
                                `user.userPermission.${permissionKey}`,
                                !formPermissions[permissionKey]
                              )
                            }
                            inline
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </TableContainer>
    </div>
  )
}

function EditProfileForm() {
  const { setFieldValue, initialValues } = useFormikContext()
  const { userIsAppAdmin, userIsClientAdmin } = useContext(FormContext)

  return (
    <>
      <Row className="mb-1">
        <Col md="6" className="px-1">
          <Field
            className="mb-1"
            name="user.name"
            label="Name"
            render={Input}
            required
          />
          <Field
            className="mb-1"
            name="user.email"
            label="Email"
            render={Input}
            type="email"
            required
          />
        </Col>
        <Col md="6" className="px-1">
          <UploadPhoto
            name="avatarImage"
            label="User photo"
            onChange={v => setFieldValue('avatarImage', v)}
            showHelperText
            helperText={`Allowed JPG or PNG. Max size of 800kB.`}
            helperText2={`This photo visible only in Locasty's dashboard.`}
            required
            maxSize={'110px'}
            initialImage={initialValues.user.avatar}
          />
        </Col>
      </Row>
      <Row>
        <Col md="6" className="px-1 mb-1">
          <Field
            className="mb-1"
            name="user.password"
            label={'New Password'}
            render={Input}
            type="password"
            required
            uuncontrolled="true"
          />
          <Field
            className="mb-1"
            name="passwordConfirmation"
            label={'Confirm New Password'}
            render={Input}
            type="password"
            required
            uncontrolled="true"
          />
        </Col>
      </Row>
      <Row className="px-1 mb-2">
        <TwoColumnResponsive>
          <Col className="mb-1" style={{ minWidth: '50%' }}>
            <h3>
              <FormattedMessage id="Email Notifications" />
            </h3>
            <div className="custom-checkbox custom-control d-flex">
              <FormikField
                id="user.userNotification.monthlyReport"
                className="custom-control-input"
                type="checkbox"
                name="user.userNotification.monthlyReport"
              />
              <CheckBoxlabel
                className="custom-control-label"
                htmlFor="user.userNotification.monthlyReport"
              >
                <div style={{ fontSize: '1.2em', fontWeight: '500' }}>
                  <FormattedMessage id="notification.monthlyReport.title" />
                </div>
                <div style={{ fontSize: '1.2em', color: '#718096' }}>
                  <FormattedMessage id="notification.monthlyReport.description" />
                </div>
              </CheckBoxlabel>
            </div>
          </Col>
          <Show condition={userIsAppAdmin || userIsClientAdmin}>
            <PermissionsTable />
          </Show>
        </TwoColumnResponsive>
      </Row>
      <CreateButton>
        <FormattedMessage id={'Update Profile'} />
      </CreateButton>
    </>
  )
}

function UserProfile() {
  const [alertVisible, setAlertVisible] = useState(false)
  const history = useHistory()
  const [formData, setFormData] = useState(null)

  const userId = useSelector(state => state?.user?.profile?.id)

  const userProfile = useSelector(state => state?.user?.profile)
  const userPermission = userProfile?.userPermission

  const userIsAppAdmin = userProfile?.role === SUPER_ADMIN

  const userIsClientAdmin = userProfile?.role === CLIENT_ADMIN

  const userIsAdmin = userProfile?.role === ADMIN

  const [, updateProfile] = useAxios(
    {
      url: `/users/profile/${userId}`,
      method: 'PATCH',
    },
    { manual: true }
  )

  const [, uploadFile] = useAxios(
    {
      url: '/upload',
      method: 'POST',
    },
    { manual: true }
  )

  const [, fetchUserData] = useAxios(`/users/${userId}`, { manual: true })

  const fillUserData = useCallback(async () => {
    const { data } = await fetchUserData()

    const user = {
      name: data.name,
      email: data.email,
      avatar: data.avatar,
      userPermission: data?.userPermission,
      permissions: data?.permissions,
      userNotification: data.userNotification,
      password: '',
    }

    setFormData({
      user,
      passwordConfirmation: '',
    })
  }, [fetchUserData])

  useEffect(() => {
    if (userId) {
      fillUserData()
    }
  }, [userId, fillUserData])

  async function handleSubmit({ user, avatarImage }) {
    let uploadedImageLink

    if (avatarImage?.type) {
      const fd = new FormData()
      fd.append('file', avatarImage)
      await uploadFile({ data: fd })
        .then(({ data }) => {
          uploadedImageLink = data.uri
        })
        .catch(e => {
          toast.error('An error occurred during upload image! Try again later.')
          console.error(e)
          throw new Error()
        })
    }

    if (avatarImage === null) {
      uploadedImageLink = ''
    }

    if (uploadedImageLink === undefined) {
      uploadedImageLink = user.avatar
    }

    try {
      const formData = {
        ...user,
        avatar: uploadedImageLink,
      }

      await updateProfile({ data: formData })

      setAlertVisible(true)
    } catch (error) {
      console.error(`Error on update profile`, error.response)
      const { data } = error.response
      toast.error(data.error ?? data.message)
    }
  }

  return (
    <Col>
      <Breadcrumb
        className="mb-2"
        paths={[
          {
            render: () => (
              <Link className="d-flex align-items-center" to="/">
                <IconHome size={20} />
              </Link>
            ),
          },
          { text: 'Update Profile' },
        ]}
      />

      <LoadableCard style={{ padding: '30px 40px' }}>
        <CardTitle>
          <FormattedMessage id={'Update Profile'} />
        </CardTitle>
        <FormContext.Provider
          value={{
            userIsAppAdmin,
            userIsClientAdmin,
            userIsAdmin,
            userPermission,
          }}
        >
          <Formik
            initialValues={formData ?? initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema()}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            enableReinitialize
          >
            {() => (
              <Form>
                <EditProfileForm />
              </Form>
            )}
          </Formik>
        </FormContext.Provider>
      </LoadableCard>
      <Alert
        type="success"
        visible={alertVisible}
        onClose={() => history.push('/')}
        confirmBtnText="Go to dashboard"
        onCancel={() => setAlertVisible(false)}
        hasCancelBtn={true}
        cancelBtnText="Close"
        body="Profile successfully updated!"
        title="Success"
      />
    </Col>
  )
}

const TwoColumnResponsive = styled.div`
  width: 100%;
  display: flex;
  gap: 1em;
  flex-direction: column;
  justify-content: space-between;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`

const CreateButton = styled(Button)`
  font-size: 18px;
  font-weight: 500;
  color: white;
  background-color: ${props => props.theme.color.primary} !important;
  margin-top: 30px;
  text-transform: uppercase;

  &:hover {
    background-color: ${props => shade(0.2, props.theme.color.primary)};
    color: white;
  }

  svg {
    margin-right: 12px;
  }
`

const CheckBoxlabel = styled.label`
  &:before {
    width: 1.3rem;
    height: 1.3rem;
  }

  &:after {
    width: 1.3rem;
    height: 1.3rem;
  }
`

export default UserProfile
